import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Wallet, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import api from '../lib/api';

// Aviso de pago mensual para el médico.
// Aparece desde la fecha de pago y se queda hasta que pulse "Ya pagué".
// El modal sale como máximo una vez al día y solo en el dashboard.

const STYLES = {
  due: 'bg-blue-50 border-blue-200 text-blue-900',
  overdue: 'bg-amber-50 border-amber-300 text-amber-900',
  late: 'bg-red-50 border-red-300 text-red-900',
};

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-PE', { day: 'numeric', month: 'long' });
};

const todayKey = () => new Date().toISOString().slice(0, 10);

export default function PaymentReminder() {
  const location = useLocation();
  const [sub, setSub] = useState(null);
  const [open, setOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [operation, setOperation] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/subscriptions/me');
      setSub(data);
    } catch {
      setSub(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Modal una vez al día, solo al abrir el dashboard (nunca en medio de una consulta)
  useEffect(() => {
    if (!sub?.show_reminder || location.pathname !== '/doctor') return;
    let seen = null;
    try { seen = localStorage.getItem('payment_modal_seen'); } catch { /* sin storage */ }
    if (seen !== todayKey()) {
      setOpen(true);
      try { localStorage.setItem('payment_modal_seen', todayKey()); } catch { /* sin storage */ }
    }
  }, [sub, location.pathname]);

  useEffect(() => {
    if (!open || qrUrl) return;
    api.get('/subscriptions/yape-qr', { responseType: 'blob' })
      .then(({ data }) => setQrUrl(URL.createObjectURL(data)))
      .catch(() => {});
  }, [open, qrUrl]);

  if (!sub?.show_reminder) return null;

  const amount = Number(sub.amount || 0).toFixed(2);
  const number = sub.yape_number || '';
  const pretty = number.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(number);
      toast.success('Número copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const report = async () => {
    setSending(true);
    try {
      await api.post('/subscriptions/me/report', { operation_number: operation || null });
      toast.success('¡Gracias! Confirmaremos tu pago pronto.');
      setOpen(false);
      setOperation('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo registrar');
    } finally {
      setSending(false);
    }
  };

  const message = sub.days_overdue === 0
    ? `Hoy vence tu suscripción de ClinIA: S/ ${amount}.`
    : `Tu suscripción de ClinIA venció el ${formatDate(sub.next_due_date)} (hace ${sub.days_overdue} ${sub.days_overdue === 1 ? 'día' : 'días'}): S/ ${amount}.`;

  return (
    <>
      <div
        className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${STYLES[sub.level] || STYLES.due}`}
        data-testid="payment-reminder"
      >
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 shrink-0" />
          <span>{message}</span>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}>Pagar con Yape</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Suscripción mensual · S/ {amount}</DialogTitle>
            <DialogDescription>
              Escanea el QR o yapea al número. Cuando termines, pulsa “Ya pagué”.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-3">
            {qrUrl ? (
              <img src={qrUrl} alt="QR de Yape" className="w-56 h-56 object-contain rounded-md border" />
            ) : (
              <div className="w-56 h-56 rounded-md border bg-muted animate-pulse" />
            )}
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold tracking-wide">{pretty}</span>
              <Button variant="outline" size="icon" onClick={copy} aria-label="Copiar número">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            {sub.yape_holder && <p className="text-xs text-muted-foreground">A nombre de {sub.yape_holder}</p>}
            <Input
              placeholder="N.º de operación (opcional)"
              value={operation}
              onChange={(e) => setOperation(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="ghost" className="flex-1" onClick={() => setOpen(false)}>Más tarde</Button>
            <Button className="flex-1" onClick={report} disabled={sending}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {sending ? 'Enviando…' : 'Ya pagué'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
